import React from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { textBlockIdsInCurrentText, useImportStationTextStore } from '../../../shared/store/importStationTextStore';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useCanShowProjectDetail } from '../../../shared/hooks/useProjectVisibility';
import { useProjectElementActions } from '../../../shared/hooks/useApi/useProjectElement';
import { peTextImportBuilder } from '../../../shared/converter/entityBuilder';
import { TextBlockData } from '../../../shared/dto/EntityRepository';
import SimpleTextBlock from '../SimpleTextBlock';
import leftIcon from '../../../asset/icon/left.png';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getPageMessage } from '../../../shared/constants/pageMessages';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';

const ImportStationTextSubMobile: React.FC = () => {
  const {
    selectedId,
    simpleSubList,
    selectedSubIdList,
    clearSimpleSubList,
  } = useImportStationTextStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { createProjectElementWithText } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();

  const handleSubmit = async () => {
    if (!project) return;
    const textBlockIds = textBlockIdsInCurrentText(selectedSubIdList, simpleSubList);
    if (!selectedId || textBlockIds.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await createProjectElementWithText(
      peTextImportBuilder(project.id, selectedId, textBlockIds),
    );
  };

  const handleClose = () => {
    clearSimpleSubList();
  };

  if (!canShowProject || !project) return null;

  return (
    <SubContainer $isActive={selectedId !== ""}>
      {selectedId !== "" && (
        <>
          <TitleWrapper>
            <TitleContainer>
              <Title>{getModalMessage('SELECT_TO_IMPORT_TEXT_BLOCK')}</Title>
            </TitleContainer>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent={true}
              backgroundImg={leftIcon}
              onClick={handleClose}
              aria-label="Back"
            />
          </TitleWrapper>
          <ContentWrapper>
            <ContentContainer>
              {simpleSubList.length === 0 ? (
                <NoContentContainer>
                  {getPageMessage('NO_DATA_FOUND')} <br />
                  {getPageMessage('ADD_FIRST', { type: getModalMessage('TEXT_BLOCKS') })}
                </NoContentContainer>
              ) : (
                simpleSubList.map((tb) => (
                  <SimpleTextBlock
                    key={tb.id}
                    data={tb as TextBlockData}
                    selectModeForNewElement
                  />
                ))
              )}
            </ContentContainer>
          </ContentWrapper>
          <HeadlessBtn
            value={getBtnName('IMPORT')}
            handleClick={handleSubmit}
            StyledBtn={SubmitButton}
          />
        </>
      )}
    </SubContainer>
  );
};

const SubContainer = styled.div<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '100vw' : '0'};
  height: 82vh;
  min-height: 82vh;
  display: flex;
  flex-direction: column;
  transition: all 0.6s ease-in-out;
`

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60vh;
  min-height: 0;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 40px;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    bottom: 0;
    background: linear-gradient(
      to top,
      rgba(228, 228, 228, 0.15),
      transparent
    );
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_01};
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 15px 0 10px 0;
`;

export default ImportStationTextSubMobile;
